import { wencesApi } from "@/api/wencesApi";

/**
 * Notas comerciales con saldo sin aplicar, por contacto.
 *
 * El aviso aparece al lado de cada cliente y cada proveedor, y una pantalla de embarque
 * muestra varios contactos a la vez: si cada uno preguntara por su cuenta serían una
 * decena de llamadas por pantalla. Acá se juntan las que caen en la misma vuelta de
 * eventos y se resuelven en una sola, con caché por contacto para el resto de la sesión.
 */

const MODULE_NAME = "nota_comercial_module";
/** Ventana para juntar los contactos que se montan juntos. */
const BATCH_DELAY_MS = 60;
/** Tope de ids por request: los contactos van en la query string. */
const MAX_IDS_PER_REQUEST = 50;

const cache = new Map();
const pending = new Map();
let queue = new Set();
let timer = null;

/** @returns {boolean} */
export function isNotaComercialModuleActive() {
  try {
    const raw = localStorage.getItem("activeModules");
    if (!raw) return false;
    const modules = JSON.parse(raw);
    if (!Array.isArray(modules)) return false;
    return modules.some((m) => m && String(m.name || "") === MODULE_NAME);
  } catch (e) {
    return false;
  }
}

function emptySummary() {
  return { count: 0, total: 0, notes: [] };
}

function resolveQueued(ids, byContact) {
  for (const id of ids) {
    const summary =
      (byContact && byContact[id]) || (byContact && byContact[String(id)]) || emptySummary();
    cache.set(id, summary);
    const waiters = pending.get(id) || [];
    pending.delete(id);
    for (const resolve of waiters) resolve(summary);
  }
}

async function flushQueue() {
  timer = null;
  const ids = [...queue];
  queue = new Set();
  if (!ids.length) return;

  for (let i = 0; i < ids.length; i += MAX_IDS_PER_REQUEST) {
    const chunk = ids.slice(i, i + MAX_IDS_PER_REQUEST);
    try {
      const { data } = await wencesApi.get("/api/nota-comercial/pending-by-contact", {
        params: { contacts: chunk.join(",") },
      });
      resolveQueued(chunk, data && data.byContact);
    } catch (e) {
      // El aviso es información extra: si el módulo no está montado o la llamada falla,
      // la pantalla sigue igual que antes y no se reintenta en cada render.
      resolveQueued(chunk, null);
    }
  }
}

/**
 * Notas con saldo sin aplicar de un contacto. Devuelve un resumen vacío si no hay
 * ninguna, si el módulo no está activo o si la consulta falla.
 * @param {string} contactId
 * @returns {Promise<{ count: number, total: number, notes: object[] }>}
 */
export function pendingNotesForContact(contactId) {
  const id = String(contactId || "").trim();
  if (!id || !isNotaComercialModuleActive()) return Promise.resolve(emptySummary());
  if (cache.has(id)) return Promise.resolve(cache.get(id));

  return new Promise((resolve) => {
    const waiters = pending.get(id) || [];
    waiters.push(resolve);
    pending.set(id, waiters);
    queue.add(id);
    if (!timer) timer = setTimeout(flushQueue, BATCH_DELAY_MS);
  });
}

/**
 * Olvida lo cacheado: al guardar o aplicar una nota el aviso tiene que volver a preguntar.
 * @param {string} [contactId] sin contacto, limpia todo.
 */
export function clearNotaComercialPendingCache(contactId) {
  const id = String(contactId || "").trim();
  if (id) cache.delete(id);
  else cache.clear();
}
